import {
  AutopilotEntityDetailsModel,
  CompanyEntityDetailsModel,
  LicenceDetails,
  LicenceDetailsState,
  States,
} from "@urbanx/agx.agencies.client";
import { toDateOnlyString } from 'utils/date'

export const stateOptions = [
  { value: States.QLD, label: States.QLD },
  { value: States.NSW, label: States.NSW },
]

export enum EntityDetailsTab {
  UrbanX = 'urbanX',
  Autopilot = 'autopilot',
}

export const entityDetailsTabOptions = [
  { id: EntityDetailsTab.UrbanX, label: 'UrbanX' },
  { id: EntityDetailsTab.Autopilot, label: 'Autopilot' },
]

export const createEmptyEntityDetails = (): CompanyEntityDetailsModel => ({
  tradingName: '',
  listingsEmail: '',
  listingsPhoneNumber: '',
  contractsEmail: '',
  complianceEmail: '',
  entityPhoneNumber: '',
  address: {
    streetAddress: '',
    suburb: '',
    state: undefined,
    postalCode: '',
  },
  companyDetails: {
    licenseeName: '',
    abn: '',
    acn: '',
    licences: [],
  },
})

export const normaliseEntityDetails = (
  details?: CompanyEntityDetailsModel
): CompanyEntityDetailsModel => ({
  ...createEmptyEntityDetails(),
  ...details,
  address: {
    ...createEmptyEntityDetails().address,
    ...details?.address,
  },
  companyDetails: {
    ...createEmptyEntityDetails().companyDetails,
    ...details?.companyDetails,
    licences: details?.companyDetails?.licences ?? [],
  },
})

export const normaliseAutopilotEntityDetails = (
  details?: AutopilotEntityDetailsModel
): AutopilotEntityDetailsModel => ({
  urbanX: normaliseEntityDetails(details?.urbanX),
  autopilot: normaliseEntityDetails(details?.autopilot),
})

export const getDetailsForTab = (
  details: AutopilotEntityDetailsModel | undefined,
  selectedTab: EntityDetailsTab
): CompanyEntityDetailsModel => {
  const normalised = normaliseAutopilotEntityDetails(details)
  const urbanX = normalised.urbanX ?? createEmptyEntityDetails()
  const autopilot = normalised.autopilot ?? createEmptyEntityDetails()

  switch (selectedTab) {
    case EntityDetailsTab.UrbanX:
      return urbanX
    case EntityDetailsTab.Autopilot:
      return autopilot
    default:
      return autopilot
  }
}

export const updateDetailsForTab = (
  details: AutopilotEntityDetailsModel | undefined,
  selectedTab: EntityDetailsTab,
  nextEntityDetails: CompanyEntityDetailsModel
): AutopilotEntityDetailsModel => {
  const normalised = normaliseAutopilotEntityDetails(details)

  switch (selectedTab) {
    case EntityDetailsTab.UrbanX:
      return { ...normalised, urbanX: nextEntityDetails }
    case EntityDetailsTab.Autopilot:
      return { ...normalised, autopilot: nextEntityDetails }
    default:
      return normalised
  }
}

export const getNextAvailableLicenceState = (
  licences: LicenceDetails[]
): LicenceDetailsState | undefined => {
  const allStates = Object.values(LicenceDetailsState)

  return allStates.find(
    (state) => !licences.some((licence) => licence.state === state)
  )
}

export const createLicenceDetails = (
  state: LicenceDetailsState | undefined
): LicenceDetails => ({
  licenceNumber: '',
  expiryDate: toDateOnlyString(new Date()),
  state,
  agreements: [],
})

export const updateLicenceAtIndex = (
  licences: LicenceDetails[],
  licenceIndex: number,
  nextLicence: LicenceDetails
): LicenceDetails[] =>
  licences.map((licence, index) =>
    index === licenceIndex ? nextLicence : licence
  )

export const removeLicenceAtIndex = (
  licences: LicenceDetails[],
  licenceIndex: number
): LicenceDetails[] => licences.filter((_, index) => index !== licenceIndex)

export const getLicenceStateOptions = (
  licences: LicenceDetails[],
  licenceIndex: number
) => {
  const selectedState = licences[licenceIndex]?.state

  return Object.values(LicenceDetailsState)
    .filter(
      (state) =>
        state === selectedState ||
        !licences.some(
          (licence, index) => index !== licenceIndex && licence.state === state
        )
    )
    .map((state) => ({ value: state, label: state }))
}
